/* eslint-disable react-hooks/exhaustive-deps */
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AppsIcon from '@mui/icons-material/Apps';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
// import StorageIcon from '@mui/icons-material/Storage';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  Box,
  // Button,
  IconButton,
  Menu,
  MenuItem,
  // Modal,
  Toolbar,
  Typography,
  Switch,
  ListItemText,
  Divider,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getUser, handleSuccessfulLogin, signOut } from '../../../services/authService';
import {
  getOrganizationById,
  getOrganisations,
  getOrganisationsLite,
  updateLastOrganization,
} from '../../../services/commonService';
import { setOrganisations } from '../../../slices/organisationSlice';
import { checkExpiry } from '../../../utility/common';
import { IUser } from '../../types/common';
import { IOrganization } from '../../types/organisation';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import Dropdown from '../Dropdown/dropdown';
import './Navbar.css';
// import Overlay from '../Overlay';
import { useNotification } from '../../../Notification/NotificationContext';
// import DiagramBpmnIndex from '../../../modules/BpmnIoDiagram/Index';
// import { closeDiagram } from '../../../slices/diagramViewerSlice';
import { DiagramViewerState } from '../../../slices/diagramViewerInterface';
// import { setCopilotOpen, setCopilotDisabled } from '../../../slices/CopilotSlice/copilotSlice';
import { useAuth } from '../../../customHooks/useAuth';
import ConfirmationModal from '../ConfirmationModal';
import { EmailOutlined } from '@mui/icons-material';
import { updateUser } from '../../../slices/userSlice';
import { resetSolution } from '../../../slices/solutionXPLRSlice';

// Enhanced types for better type safety
interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  state?: any;
}

interface RootState {
  user: IUser;
  diagramViewer: DiagramViewerState;
}

interface CompanyDropdownState {
  selectedOrganization: IOrganization | null;
  searchValue: string;
  filteredOptions: IOrganization[];
}

export interface SlimOrganisation {
  id: string;
  name: string;
  summary?: string;
  taxonomy?: {
    options: string[];
    value: string[];
  };
  customTaxonomy?: {
    ids: string[];
  };
}

// Constants
const EMPTY_ORG_ID = 'Empty';
const EXPIRY_CHECK_INTERVAL = 60000; // 1 minute
const LOCAL_STORAGE_ORG_KEY = 'current_organization';

const Navbar: React.FC = () => {
  const location = useLocation();
  const user = useSelector((state: RootState) => state.user);
  const viewAccess = user.permissions?.new_solution?.view_solution_requests_page;
  // const { isOpen=false } = useSelector<RootState, DiagramViewerState>((state) => state.diagramViewer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAuth();

  const fetchUser = useCallback(async () => {
    const user = await getUser(true);
    if (user?.id) {
      dispatch(updateUser(user));
    }
  }, [dispatch]);

  // Fetch user data on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Track previous base route to detect module navigation changes
  const prevBaseRouteRef = useRef<string>('');

  // Refresh user data when navigating to a different module route
  useEffect(() => {
    // Extract base route (e.g., '/process', '/ideation-xplr', '/data-xplr')
    const baseRoute = location.pathname.split('/').slice(0, 2).join('/') || '/';

    // Only refresh if we've navigated to a different module route
    if (prevBaseRouteRef.current && prevBaseRouteRef.current !== baseRoute) {
      fetchUser();
    }

    prevBaseRouteRef.current = baseRoute;
  }, [location.pathname, fetchUser]);

  // Account menu state
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Company dropdown state
  const [companyState, setCompanyState] = useState<CompanyDropdownState>({
    selectedOrganization: null,
    searchValue: '',
    filteredOptions: [],
  });

  const { showAlerts, setShowAlerts } = useNotification();

  // Organizations data
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [isProcessingPostSignin, setIsProcessingPostSignin] = useState(false);
  const [intendedRedirectPath, setIntendedRedirectPath] = useState<string | null>(null);
  const postLoginOrgIdRef = useRef<string | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState<boolean>(true);
  const hydratedOrgIdsRef = useRef<Set<string>>(new Set());

  // Mapping between navigation paths and user_modules keys
  const pathToModuleMap: Record<string, keyof NonNullable<IUser['user_modules']>> = {
    '/': 'dashboard',
    '/process': 'process_xplr',
    '/process-xplr': 'process_xplr',
    '/ideation-xplr': 'ideation_xplr',
    '/solution-xplr': 'solution_xplr',
    '/portfolio': 'portfolio_xplr',
    '/data-xplr': 'data_xplr',
  };

  // Helper function to check if user has access to a module
  const hasModuleAccess = useCallback(
    (path: string): boolean => {
      const moduleKey = pathToModuleMap[path];
      if (!moduleKey) return true; // Allow access if path is not in the map (e.g., other routes)
      return user.user_modules?.[moduleKey] === true;
    },
    [user.user_modules]
  );

  // Memoized navigation items
  const navItems = useMemo<NavItem[]>(() => {
    const allNavItems: NavItem[] = [
      {
        path: '/',
        label: 'COI Dashboard',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.84911 0H7.39656C8.41819 0 9.24567 1.02163 9.24567 1.84911V5.91727C9.24567 6.9389 8.41819 7.76638 7.39656 7.76638H1.84911C0.827478 7.76638 0 6.74475 0 5.91727V1.84911C0 0.827478 0.827478 0 1.84911 0Z"
              fill="currentColor"
            />
            <path
              d="M12.531 12.2031H18.0784C19.1001 12.2031 19.9275 13.2248 19.9275 14.0522V18.1204C19.9275 19.142 19.1001 19.9695 18.0784 19.9695H12.531C11.5093 19.9695 10.6819 18.9479 10.6819 18.1204V14.0522C10.6819 13.0306 11.5093 12.2031 12.531 12.2031Z"
              fill="currentColor"
            />
            <path
              d="M12.531 0H18.0784C19.1001 0 19.9275 1.02163 19.9275 1.84911V8.87585C19.9275 9.89748 19.1001 10.725 18.0784 10.725H12.531C11.5093 10.725 10.6819 9.70332 10.6819 8.87585V1.84911C10.6819 0.827478 11.5093 0 12.531 0Z"
              fill="currentColor"
            />
            <path
              d="M1.84911 9.27344H7.39656C8.41819 9.27344 9.24567 10.2951 9.24567 11.1225V18.1493C9.24567 19.1709 8.41819 19.9984 7.39656 19.9984H1.84911C0.827478 19.9984 0 18.9768 0 18.1493V11.1225C0 10.1009 0.827478 9.27344 1.84911 9.27344Z"
              fill="currentColor"
            />
          </svg>
        ),
      },

      // { path: '/industry', label: 'Industry XPLR', icon: <StorageIcon fontSize="small" /> },
      { path: '/process', label: 'Process XPLR', icon: <AccountTreeIcon fontSize="small" /> },
      {
        path: '/data-xplr',
        label: 'Data XPLR',
        icon: (
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.5024 3.03354C17.5024 4.70955 13.584 6.06797 8.75121 6.06797C3.91837 6.06797 0 4.70951 0 3.03354C0 1.35839 3.91837 0 8.75121 0C13.584 0 17.5024 1.35846 17.5024 3.03354Z"
              fill="currentColor"
            />
            <path
              d="M17.5024 14.9691C17.5024 16.3994 13.7591 18.0002 8.75121 18.0002C3.7427 18.0002 0 16.3995 0 14.9691V11.3125C1.87866 12.6917 5.38577 13.4177 8.75121 13.4177C12.1167 13.4177 15.6236 12.6909 17.5024 11.3125V14.9691Z"
              fill="currentColor"
            />
            <path
              d="M8.75121 12.0279C3.7427 12.0279 0 10.4272 0 8.99685V5.34375C1.8778 6.72299 5.38577 7.4498 8.75121 7.4498C12.1167 7.4498 15.6236 6.72299 17.5024 5.34375V8.99685C17.5024 10.4271 13.7591 12.0279 8.75121 12.0279Z"
              fill="currentColor"
            />
          </svg>
        ),
        state: { reset: true },
      },
      {
        path: '/ideation-xplr',
        label: 'Ideation XPLR',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 17 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.0319 13.8564H3.77288C3.7014 13.8564 3.64303 13.8095 3.63112 13.7404C3.48935 13.1252 3.10098 12.5346 2.52321 12.0589C0.919714 10.7605 3.42093e-05 8.847 3.42093e-05 6.79394C-0.0118787 3.04899 3.08912 0 6.89641 0C8.07581 0 9.21946 0.289432 10.2093 0.834303C10.2677 0.869458 10.3035 0.938594 10.2796 1.0089C10.2332 1.13546 10.2213 1.2292 10.2213 1.33348V1.4612C10.2213 1.4612 10.1974 1.55377 10.1498 1.58776C10.1033 1.61119 10.0557 1.61119 10.0092 1.58776L9.87935 1.51862C9.63156 1.37918 9.34802 1.3452 9.06568 1.41433C8.79406 1.48347 8.5701 1.65806 8.44143 1.9018L7.10836 4.1622C6.96778 4.40594 6.93204 4.68366 7.00233 4.96255C7.07381 5.218 7.25012 5.43829 7.49791 5.57658L7.61585 5.64689C7.61585 5.64689 7.68614 5.71602 7.68614 5.76289C7.68614 5.80859 7.66231 5.85546 7.61585 5.89062L7.49791 5.97147C7.23821 6.11092 7.06189 6.33121 7.00233 6.5972C6.93204 6.87609 6.96778 7.15381 7.10836 7.39755L8.44143 9.65912C8.582 9.90168 8.79404 10.0646 9.06568 10.1454C9.33729 10.2157 9.61963 10.1806 9.86742 10.0411L10.0092 9.97199C10.0092 9.97199 10.1033 9.94855 10.1498 9.97199C10.1974 9.99543 10.2212 10.0411 10.2212 10.0997V10.2274C10.2212 10.7946 10.693 11.2703 11.2589 11.2703H11.7771C11.8354 11.2703 11.895 11.3055 11.9188 11.3629C11.9415 11.4215 11.9188 11.4789 11.8831 11.5258C11.671 11.7344 11.4709 11.9195 11.2696 12.0707C10.693 12.5347 10.3153 13.1148 10.1736 13.7522C10.1616 13.8214 10.1033 13.8682 10.0318 13.8682L10.0319 13.8564ZM6.89642 20C5.14044 20 3.70121 18.5857 3.70121 16.8466V16.4752H3.58446C3.13652 16.4752 2.77079 16.1154 2.77079 15.6748C2.77079 15.2342 3.14725 14.8745 3.58446 14.8745H10.2094C10.6574 14.8745 11.0231 15.2342 11.0231 15.6748C11.0231 16.1154 10.6693 16.4752 10.2094 16.4752H10.1034V16.8466C10.1034 18.5856 8.66564 20 6.89642 20ZM15.9862 5.20621C15.786 5.32222 15.6562 5.55425 15.6562 5.77452V5.78624C15.6562 6.02997 15.786 6.25028 15.9862 6.36628L17 6.93459L15.7968 8.97469L14.8187 8.40638H14.7949C14.5828 8.29037 14.3231 8.29037 14.123 8.40638C13.9109 8.52239 13.793 8.74269 13.793 8.97469V10.123H11.3997V8.97469C11.3997 8.73096 11.271 8.52238 11.0697 8.40638C10.8576 8.29037 10.5979 8.29037 10.3978 8.40638L9.384 8.97469L8.18197 6.93459L9.19577 6.36628C9.39591 6.25027 9.52575 6.01824 9.52575 5.78624C9.52575 5.55422 9.3959 5.33393 9.19577 5.21793L8.18197 4.64962L9.384 2.60952L10.3978 3.17666C10.5979 3.29267 10.8576 3.29267 11.0697 3.17666C11.271 3.06065 11.3997 2.84035 11.3997 2.60952V1.46116H13.793V2.60952C13.793 2.62006 13.793 2.63178 13.8049 2.6435C13.8049 2.85208 13.9109 3.02666 14.0884 3.14269C14.0932 3.14855 14.0992 3.1509 14.1051 3.15441C14.1111 3.15676 14.117 3.16027 14.123 3.16613C14.3231 3.28214 14.5828 3.28214 14.7949 3.16613L15.7968 2.59782L17 4.63792L15.9862 5.20621ZM13.5226 8.10524C14.2409 7.82753 14.783 7.23578 14.9843 6.50458C15.0665 6.296 15.0903 6.06397 15.0903 5.83314C15.0903 5.32224 14.9247 4.83595 14.6305 4.42932C14.2064 3.8153 13.4988 3.4087 12.697 3.38641C12.6613 3.3747 12.6255 3.3747 12.591 3.3747C11.2115 3.3747 10.0916 4.47619 10.0916 5.83312C10.0916 7.18888 11.2115 8.29037 12.591 8.29037C12.921 8.29037 13.2271 8.22125 13.5226 8.10524ZM12.5969 7.12095H12.6029H12.5909H12.5969ZM11.2936 5.83314C11.2936 6.53857 11.8798 7.11741 12.5969 7.12095C13.1008 7.11861 13.5583 6.84089 13.7692 6.40148C13.8645 6.21633 13.9109 6.04174 13.9109 5.83317C13.9109 5.48513 13.7692 5.16055 13.5226 4.91799C13.2867 4.68597 12.9448 4.54653 12.6029 4.54653C11.8833 4.54653 11.2936 5.12654 11.2936 5.83314Z"
              fill="currentColor"
            />
          </svg>
        ),
      },
      { path: '/solution-xplr', label: 'Solution XPLR', icon: <EditIcon fontSize="small" /> },
      { path: '/portfolio', label: 'Portfolio XPLR', icon: <AppsIcon fontSize="small" /> },
      // {
      //   path: '/design-nexus',
      //   label: 'Functional Design XPLR',
      //   icon: <DesignServicesIcon fontSize="small" />,
      // }
    ];

    // Filter navigation items based on user_modules permissions
    return allNavItems.filter((item) => hasModuleAccess(item.path));
  }, [hasModuleAccess]);

  // Get current organization from localStorage
  const getCurrentOrganizationId = useCallback((): string => {
    return localStorage.getItem(LOCAL_STORAGE_ORG_KEY) || EMPTY_ORG_ID;
  }, []);

  // Set current organization in localStorage
  const setCurrentOrganizationId = useCallback((orgId: string): void => {
    localStorage.setItem(LOCAL_STORAGE_ORG_KEY, orgId);
  }, []);

  const extractOrgIdFromUrl = useCallback((url: string): string | null => {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.searchParams.get('orgId');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return null;
    }
  }, []);

  // Filter organizations based on search value
  const filterOrganizations = useCallback(
    (orgs: IOrganization[], searchTerm: string): IOrganization[] => {
      if (!searchTerm.trim()) return orgs;

      return orgs.filter((org) => org.name.toLowerCase().includes(searchTerm.toLowerCase()));
    },
    []
  );

  // Find organization by ID
  const findOrganizationById = useCallback(
    (orgs: IOrganization[], orgId: string): IOrganization | undefined => {
      return orgs.find((org) => org.id === orgId);
    },
    []
  );

  // Replace or append an organization with its latest payload
  const upsertOrganization = useCallback(
    (orgs: IOrganization[], updatedOrg: IOrganization): IOrganization[] => {
      const index = orgs.findIndex((org) => org.id === updatedOrg.id);
      if (index === -1) {
        return [...orgs, updatedOrg];
      }
      const next = [...orgs];
      next[index] = { ...next[index], ...updatedOrg };
      return next;
    },
    []
  );

  const initializeSelectedOrganization = useCallback(
    (orgs: IOrganization[], intendedRoute?: string): IOrganization => {
      if (intendedRoute) {
        const orgIdFromUrl = extractOrgIdFromUrl(intendedRoute);
        if (orgIdFromUrl) {
          const orgFromUrl = findOrganizationById(orgs, orgIdFromUrl);
          if (orgFromUrl) {
            setCurrentOrganizationId(orgFromUrl.id);
            return orgFromUrl;
          }
        }
      }

      const currentOrgId = getCurrentOrganizationId();

      if (currentOrgId === EMPTY_ORG_ID || !findOrganizationById(orgs, currentOrgId)) {
        // If no valid organization is stored, select the first one
        const firstOrg = orgs[0];
        if (firstOrg) {
          setCurrentOrganizationId(firstOrg.id);
          return firstOrg;
        }
      }

      return findOrganizationById(orgs, currentOrgId) || orgs[0];
    },
    [getCurrentOrganizationId, setCurrentOrganizationId, findOrganizationById, extractOrgIdFromUrl]
  );

  const clearQueryParams = () => {
    const params = new URLSearchParams(location.search);

    // Remove only the params you set earlier
    params.delete('step');
    params.delete('activeStep');
    params.delete('solutionId');

    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname; // keep base path if no params left

    window.history.replaceState(null, '', newUrl);
  };

  // Handle organization selection
  const handleOrganizationSelection = useCallback(
    async (selectedOrgName: string): Promise<void> => {
      const selectedOrg = organizations.find((org) => org.name === selectedOrgName);

      if (selectedOrg && selectedOrg.id !== companyState.selectedOrganization?.id) {
        setCurrentOrganizationId(selectedOrg.id);
        setCompanyState((prev) => ({
          ...prev,
          selectedOrganization: selectedOrg,
          searchValue: '', // Clear search after selection
        }));

        try {
          await updateLastOrganization(selectedOrg.id);
        } catch (error) {
          console.error('Error updating last used organization:', error);
        }

        clearQueryParams();
        dispatch(resetSolution());
        // Reload page to reflect organization change
        window.location.reload();
      }
    },
    [
      organizations,
      companyState.selectedOrganization,
      setCurrentOrganizationId,
      updateLastOrganization,
    ]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string): void => {
      const filteredOptions = filterOrganizations(organizations, value);

      setCompanyState((prev) => ({
        ...prev,
        searchValue: value,
        filteredOptions,
      }));
    },
    [organizations, filterOrganizations]
  );

  // Account menu handlers
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const handleSignOut = useCallback((): void => {
    setShowSignOutDialog(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const confirmSignOut = () => {
    localStorage.setItem('LogoutToast', 'Sign Out');
    signOut();
  };

  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const cancelSignOut = () => setShowSignOutDialog(false);

  // Enhanced post-signin handler
  const handlePostSigninRedirect = useCallback(async () => {
    if (isProcessingPostSignin || organizations.length === 0) return;

    setIsProcessingPostSignin(true);

    try {
      const redirectUrl = handleSuccessfulLogin();

      if (redirectUrl && redirectUrl !== '/') {
        const orgIdFromUrl = extractOrgIdFromUrl(redirectUrl);
        const org = findOrganizationById(organizations, orgIdFromUrl || '');

        if (org) {
          setCurrentOrganizationId(org.id);
          postLoginOrgIdRef.current = org.id;
          setCompanyState((prev) => ({
            ...prev,
            selectedOrganization: org,
          }));
          setIntendedRedirectPath(redirectUrl);
          return;
        }
      }

      setIntendedRedirectPath(redirectUrl || '/');
    } catch (error) {
      console.error('Post-signin redirect error:', error);
      setIntendedRedirectPath('/');
    } finally {
      setIsProcessingPostSignin(false);
    }
  }, [
    isProcessingPostSignin,
    organizations,
    extractOrgIdFromUrl,
    findOrganizationById,
    setCurrentOrganizationId,
  ]);

  useEffect(() => {
    const expectedOrgId = postLoginOrgIdRef.current;

    if (
      isAuthenticated &&
      intendedRedirectPath &&
      companyState.selectedOrganization?.id &&
      expectedOrgId &&
      companyState.selectedOrganization.id === expectedOrgId
    ) {
      navigate(intendedRedirectPath);
      setIntendedRedirectPath(null);
      postLoginOrgIdRef.current = null;
    }
  }, [companyState.selectedOrganization?.id, isAuthenticated, intendedRedirectPath, navigate]);

  // Fetch organizations on component mount and user change
  useEffect(() => {
    let isMounted = true;

    const fetchOrganizations = async (): Promise<void> => {
      try {
        let fetchedOrgs: IOrganization[] = [];
        try {
          fetchedOrgs = await getOrganisationsLite();
        } catch (liteError) {
          console.error(
            'Error fetching organizations-lite, falling back to organizations:',
            liteError
          );
          fetchedOrgs = await getOrganisations();
        }
        if (!isMounted) return;
        const slimOrgs: IOrganization[] = fetchedOrgs
          .map(({ id, name, summary, taxonomy, customTaxonomy, created_at, deleted_at }) => ({
            id,
            name,
            summary,
            taxonomy: taxonomy
              ? {
                  value: taxonomy.value || [],
                  options: taxonomy.options || [],
                }
              : undefined,
            customTaxonomy: customTaxonomy
              ? {
                  ids: customTaxonomy.ids || [],
                }
              : undefined,
            created_at: created_at ?? null,
            deleted_at: deleted_at ?? null,
          }))
          .sort((a: IOrganization, b: IOrganization) => a.name.localeCompare(b.name));
        dispatch(setOrganisations(slimOrgs));
        setOrganizations(slimOrgs);
        if (fetchedOrgs.length > 0) {
          const intendedRoute = sessionStorage.getItem('intendedRoute');
          const selectedOrg = initializeSelectedOrganization(slimOrgs, intendedRoute || undefined);
          const filteredOptions = filterOrganizations(slimOrgs, '');
          dispatch(
            updateUser({
              ...user,
              organizations: filteredOptions.map(
                (data: { id: string; name: string; taxonomy?: any }) => ({
                  id: data.id,
                  name: data.name,
                })
              ),
            })
          );
          setCompanyState({
            selectedOrganization: selectedOrg,
            searchValue: '',
            filteredOptions,
          });

          // Hydrate selected organization with full taxonomy/customTaxonomy if not already present
          if (selectedOrg?.id) {
            try {
              const fullOrg = await getOrganizationById(selectedOrg.id);
              if (!isMounted) return;
              const mergedOrgs = upsertOrganization(slimOrgs, fullOrg);
              dispatch(setOrganisations(mergedOrgs));
              setOrganizations(mergedOrgs);
              setCompanyState((prev) => ({
                ...prev,
                selectedOrganization:
                  mergedOrgs.find((org) => org.id === selectedOrg.id) || prev.selectedOrganization,
              }));
            } catch (fullOrgError: any) {
              if (fullOrgError?.status === 404 || fullOrgError?.status === 403) {
                console.error(
                  'Selected organization is inaccessible. Please reselect an organization.'
                );
              } else {
                console.error('Error fetching selected organization details:', fullOrgError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    };

    fetchOrganizations();

    return () => {
      isMounted = false;
    };
  }, [
    user.userId,
    user?.user_modules,
    dispatch,
    initializeSelectedOrganization,
    filterOrganizations,
    upsertOrganization,
    getOrganizationById,
  ]);

  // Fetch full organization details for the currently selected org when taxonomy is missing
  useEffect(() => {
    let isMounted = true;
    const selectedOrgId = companyState.selectedOrganization?.id;
    if (!selectedOrgId) return undefined;

    const selectedOrg = organizations.find((org) => org.id === selectedOrgId);
    const hasTaxonomy =
      Array.isArray(selectedOrg?.taxonomy?.value) && selectedOrg?.taxonomy?.value.length > 0;
    if (hasTaxonomy) return undefined;

    if (hydratedOrgIdsRef.current.has(selectedOrgId)) return undefined;
    hydratedOrgIdsRef.current.add(selectedOrgId);

    const hydrateSelectedOrganization = async () => {
      try {
        const fullOrg = await getOrganizationById(selectedOrgId);
        if (!isMounted) return;
        const mergedOrgs = upsertOrganization(organizations, fullOrg);
        dispatch(setOrganisations(mergedOrgs));
        setOrganizations(mergedOrgs);
      } catch (error: any) {
        if (error?.status === 404 || error?.status === 403) {
          console.error('Selected organization is inaccessible. Please reselect an organization.');
          localStorage.removeItem(LOCAL_STORAGE_ORG_KEY);
          setCompanyState((prev) => ({
            ...prev,
            selectedOrganization: null,
          }));
        } else {
          console.error('Error fetching organization details:', error);
        }
      }
    };

    hydrateSelectedOrganization();

    return () => {
      isMounted = false;
    };
  }, [
    companyState.selectedOrganization?.id,
    organizations,
    upsertOrganization,
    dispatch,
    getOrganizationById,
  ]);

  useEffect(() => {
    if (isAuthenticated && organizations.length > 0 && !isProcessingPostSignin) {
      handlePostSigninRedirect();
    }
  }, [isAuthenticated, organizations.length, handlePostSigninRedirect, isProcessingPostSignin]);

  // Setup expiry check interval
  useEffect(() => {
    const interval = setInterval(checkExpiry, EXPIRY_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const selectedOrg = organizations.find(
      (org) => org.id === localStorage.getItem(LOCAL_STORAGE_ORG_KEY)
    );
    if (selectedOrg && selectedOrg.id !== companyState.selectedOrganization?.id) {
      setCurrentOrganizationId(selectedOrg.id);
      setCompanyState((prev) => ({
        ...prev,
        selectedOrganization: selectedOrg,
        searchValue: '',
      }));
    }
  }, [window.location.href]);

  // useEffect(() => {
  //   const lastOrganizationName = organizations.find(
  //     (org) => org.id === user.last_used_organization_id
  //   )?.name;

  //   if (!lastOrganizationName) return;

  //   handleOrganizationSelection(lastOrganizationName);
  // }, [organizations, user?.last_used_organization_id, isAuthenticated]);

  // Memoized dropdown options for performance
  const dropdownOptions = useMemo(() => {
    const options = companyState.searchValue ? companyState.filteredOptions : organizations;
    return options.map((org) => org.name);
  }, [companyState.searchValue, companyState.filteredOptions, organizations]);

  // const handleCopilotOpen = () => {
  //   dispatch(setCopilotDisabled(false)); // Re-enable Copilot
  //   dispatch(setCopilotOpen(true));
  // }

  const isMenuOpen = Boolean(anchorEl);
  const selectedOrgName = companyState.selectedOrganization?.name || 'Select Organization';

  const isMobile = useMediaQuery('(max-width:1300px)');

  const [drawerOpen, setDrawerOpen] = useState<false | 'nav' | 'account'>(false);

  const isNavItemActive = useCallback(
    (itemPath: string): boolean => {
      if (itemPath === '/') {
        return location.pathname === '/';
      }

      if (location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`)) {
        return true;
      }

      const currentModule = pathToModuleMap[location.pathname];
      const itemModule = pathToModuleMap[itemPath];
      if (currentModule && itemModule && currentModule === itemModule) {
        return true;
      }

      return false;
    },
    [location.pathname, pathToModuleMap]
  );
  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      className="navbar"
      sx={{
        backgroundColor: '#ffffff',
        width: '100%',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1298,
      }}
    >
      {isMobile ? (
        <Toolbar
          className="toolbar"
          disableGutters
          sx={{ minHeight: '60px', height: '60px', pl: 1 }}
        >
          {/* Left side → Logo + Hamburger */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <IconButton onClick={() => setDrawerOpen(drawerOpen === 'nav' ? false : 'nav')}>
              <MenuIcon />
            </IconButton>
            <IconButton color="primary" component={Link} to="/" aria-label="Home">
              <img src="/images/logo.svg" alt="Company Logo" />
            </IconButton>
          </Box>

          {/* Right side → 3 buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Copilot */}
            {/* <IconButton color="primary" onClick={handleCopilotOpen}>
              <img src="/images/Chatbot-black.svg" alt="copilot Logo" />
            </IconButton> */}

            {/* Approval */}
            {viewAccess && (
              <IconButton component={Link} to="/approval-requests">
                <EmailOutlined fontSize="medium" />
              </IconButton>
            )}

            {/* Account menu as hamburger */}
            <IconButton onClick={() => setDrawerOpen(drawerOpen === 'account' ? false : 'account')}>
              <Avatar sx={{ width: 50, height: 50, color: '#313131', bgcolor: '#ffffff' }}>
                <AccountCircleIcon fontSize="large" />
              </Avatar>
            </IconButton>
          </Box>

          {/* Navigation Drawer */}
          <Drawer anchor="left" open={drawerOpen === 'nav'} onClose={() => setDrawerOpen(false)}>
            <Box sx={{ width: 250, p: 2, mt: 5 }}>
              <List>
                {navItems?.map((item) => (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      onClick={() => {
                        setDrawerOpen(false);
                      }}
                      className={`nav-link ${isNavItemActive(item.path) ? 'active' : ''}`}
                      selected={isNavItemActive(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>

          {/* Account Drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen === 'account'}
            onClose={() => setDrawerOpen(false)}
          >
            <Box sx={{ width: 250, p: 2, mt: 5 }}>
              {/* <Typography variant="h6" sx={{ mb: 2 }}>
                Account Menu
              </Typography>
              <Divider /> */}
              {/* Enterprise Context */}
              <ListItem
                component={Link}
                to="/enterprise-context"
                onClick={() => setDrawerOpen(false)}
              >
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText sx={{ color: 'black' }} primary="Enterprise Context" />
              </ListItem>

              <Divider />

              {/* Company Dropdown */}
              <ListItem disablePadding>
                <ListItemButton disableRipple sx={{ px: 2, py: 1 }}>
                  <Dropdown
                    options={dropdownOptions}
                    onOptionSelect={(option) => {
                      handleOrganizationSelection(option);
                      setDrawerOpen(false);
                    }}
                    searchValue={companyState.searchValue}
                    searchHandler={handleSearchChange}
                    fullWidth
                    children={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <LanguageIcon />
                        </ListItemIcon>
                        <ListItemText sx={{ p: '0px  8px' }} primary={selectedOrgName} />
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>

              <Divider />

              {/* Show Alerts */}
              <ListItem>
                <ListItemText primary="Show alerts" />
                <Switch
                  edge="end"
                  checked={showAlerts}
                  onChange={(e) => setShowAlerts(e.target.checked)}
                />
              </ListItem>

              <Divider />

              {/* Sign Out */}
              <ListItem disablePadding>
                <ListItemButton onClick={handleSignOut}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign Out" />
                </ListItemButton>
              </ListItem>

              <Divider />
            </Box>
          </Drawer>
        </Toolbar>
      ) : (
        <Toolbar
          className="toolbar"
          disableGutters
          sx={{ minHeight: '60px', height: '60px', pl: 1 }}
        >
          {/* Logo Section */}
          <Box className="logo-nav">
            <IconButton color="primary" component={Link} to="/" aria-label="Home">
              <img src="/images/logo.svg" alt="Company Logo" />
            </IconButton>
          </Box>

          {/* Navigation Links */}
          {!loadingOrgs && (
            <Box className="nav-links">
              {navItems.map((item) => {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    state={item.state || {}}
                    style={{ textDecoration: 'none' }}
                  >
                    <Box
                      className={`nav-link ${isNavItemActive(item.path) ? 'active' : ''}`}
                      role="button"
                      tabIndex={0}
                    >
                      {item.icon}
                      <Typography className="nav-label" variant="body1" sx={{ ml: 0.5 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Link>
                );
              })}
            </Box>
          )}

          {/* <Box className="copilot-icon" sx={{marginRight: '4px'}}>
          <IconButton
            color="primary"
            onClick={handleCopilotOpen}
            aria-label="Open Copilot"
          >
            <img src="/images/Chatbot-black.svg" alt="copilot Logo" />
          </IconButton>
        </Box> */}

          {/* Right Navigation Section */}
          <Box className="right-nav-links" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Enterprise Context Button */}
            <CustomTooltip title="Enterprise Context" variant="bottom" offset={[0, 0]}>
              <IconButton
                component={Link}
                to={'/enterprise-context'}
                aria-label="Open Enterprise Context"
              >
                <BusinessIcon fontSize="medium" />
              </IconButton>
            </CustomTooltip>
            {viewAccess && (
              <CustomTooltip title="Approval Requests" variant="bottom" offset={[0, 0]}>
                <IconButton
                  component={Link}
                  to={'/approval-requests'}
                  aria-label="Open solution approval requests"
                >
                  <EmailOutlined fontSize="medium" />
                </IconButton>
              </CustomTooltip>
            )}
            {/* Company Dropdown */}
            <CustomTooltip title="" variant="bottom" offset={[0, 0]}>
              <Box className="company-info" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Dropdown
                  options={dropdownOptions}
                  selectedOption={selectedOrgName}
                  onOptionSelect={handleOrganizationSelection}
                  searchValue={companyState.searchValue}
                  searchHandler={handleSearchChange}
                  fullWidth
                  children={
                    <IconButton aria-label="Select Organization">
                      <LanguageIcon fontSize="medium" />
                    </IconButton>
                  }
                />
              </Box>
            </CustomTooltip>

            {/* Account Menu */}
            <CustomTooltip title="Account Menu" variant="bottom" offset={[0, 0]}>
              <IconButton onClick={handleMenuOpen} aria-label="Open Account Menu">
                <Avatar sx={{ width: 50, height: 50, color: '#313131', bgcolor: '#ffffff' }}>
                  <AccountCircleIcon fontSize="large" />
                </Avatar>
              </IconButton>
            </CustomTooltip>

            {/* Account Menu Dropdown */}
            <Menu
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableScrollLock
            >
              {/* Show alerts toggle */}
              <MenuItem disableRipple>
                <ListItemText>Show alerts</ListItemText>
                <Switch
                  edge="end"
                  checked={showAlerts}
                  onChange={(e) => setShowAlerts(e.target.checked)}
                  slotProps={{ input: { 'aria-label': 'Show alerts' } }}
                />
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleSignOut}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small">
                    <LogoutIcon fontSize="medium" />
                  </IconButton>
                  <Typography>Sign Out</Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      )}

      <ConfirmationModal
        isOpen={showSignOutDialog}
        title="Sign Out"
        description="Are you sure you want to sign out?"
        primaryButtonText="Yes"
        secondaryButtonText="No"
        onPrimaryAction={confirmSignOut}
        onSecondaryAction={cancelSignOut}
        onClose={cancelSignOut}
      />
      {/* <Overlay
        open={isOpen}
        onClose={() => {
            dispatch(closeDiagram());
        }}
        showCloseButton={false}
      >
        <DiagramBpmnIndex />
      </Overlay> */}
    </AppBar>
  );
};

export default Navbar;
